import { useState, useCallback, useMemo } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { getPresetRanges, checkIfPresetRange, formatDateForApi, createCustomPreset } from '../lib/date-utils';

export interface DateRangeValue {
  period?: string;
  fromDate?: string;
  toDate?: string;
}

interface UseDateRangePickerProps {
  defaultPreset?: string;
  defaultFromDate?: string;
  defaultToDate?: string;
  onChange?: (value: DateRangeValue) => void;
}

export const useDateRangePicker = ({
  defaultPreset = 'L6M',
  defaultFromDate,
  defaultToDate,
  onChange
}: UseDateRangePickerProps = {}) => {
  const { t } = useTranslation();
  const presetRanges = useMemo(() => getPresetRanges(t), [t]);
  const [open, setOpen] = useState(false);

  const getDefaultRange = useCallback(() => {
    if (defaultPreset === 'CR' && defaultFromDate && defaultToDate) {
      const customPreset = createCustomPreset(defaultFromDate, defaultToDate, t);
      return customPreset?.getValue();
    }

    const preset = presetRanges.find((p) => p.id === defaultPreset);
    return preset?.getValue();
  }, [defaultPreset, defaultFromDate, defaultToDate, presetRanges, t]);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(getDefaultRange);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(defaultPreset);

  const getDateRangeValue = useCallback(
    (range: DateRange | undefined, matchingPreset: string | null): DateRangeValue => {
      if (matchingPreset) {
        return { period: matchingPreset };
      }

      if (range?.from && range?.to) {
        return {
          period: 'CR',
          fromDate: formatDateForApi(range.from),
          toDate: formatDateForApi(range.to)
        };
      }

      return {};
    },
    []
  );

  const handleRangeChange = useCallback(
    (range?: DateRange) => {
      setDateRange(range);

      const matchingPreset = checkIfPresetRange(presetRanges, range);
      setSelectedPreset(matchingPreset);
    },
    [presetRanges]
  );

  const handlePresetSelect = useCallback(
    (presetId: string) => {
      const preset = presetRanges.find((p) => p.id === presetId);
      if (!preset) return;

      const range = preset.getValue();
      setDateRange(range);
      setSelectedPreset(presetId);
      if (range.from && range.to) {
        onChange?.({
          period: presetId,
          fromDate: formatDateForApi(range?.from),
          toDate: formatDateForApi(range?.to)
        });
      }
      setOpen(false);
    },
    [presetRanges, onChange]
  );

  const handleCancel = useCallback(() => {
    const preset = presetRanges.find((p) => p.id === defaultPreset);
    const range = preset?.getValue();
    setDateRange(range);
    setSelectedPreset(defaultPreset);
    setOpen(false);
  }, [presetRanges, defaultPreset]);

  const handleApply = useCallback(() => {
    if (onChange) {
      const value = getDateRangeValue(dateRange, selectedPreset);
      onChange(value);
    }
    setOpen(false);
  }, [onChange, dateRange, selectedPreset, getDateRangeValue]);

  return {
    // State
    open,
    dateRange,
    selectedPreset,
    presetRanges,

    // Handlers
    setOpen,
    handleRangeChange,
    handlePresetSelect,
    handleCancel,
    handleApply
  };
};
